import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Checkbox, ListItemText, MenuItem, Select, SelectChangeEvent, TextField } from '@mui/material';
import { FiPlus } from 'react-icons/fi';
import { HiChevronDown } from 'react-icons/hi';
import moment from 'moment-timezone';
import { useLocation, useSearchParams } from 'react-router-dom';

import useRoleNavigate from '@brands/hooks/useRoleNavigate';
import { selectAuth } from '@brands/store/selectors/auth';
import { ColumnVisibilityKey } from '@brands/store/slices/columnVisibilitySlice';
import { TablePaginationKey } from '@brands/store/slices/tablePaginationSlice';
import {
  getSessionArray,
  getSessionNumber,
  getSessionString,
  readSessionTableState,
  writeSessionTableState,
} from '@brands/Utils/sessionTableState';
import type { IListCasesParams } from '@brands/services/cases/listAllCases';
import { listAllClientTags } from '@brands/services/identity/listAllClientTags';
import { getClientDivisionOptions } from '@brands/services/identity/clientDivisionOptions';
import MinimalDateRangePicker from '../../../../Components/MinimalDateRangePicker/MinimalDateRangePicker';
import { useWindowSize } from '../../../../hooks';
import useCareAdvisorCases from '../../../../hooks/useCareAdvisorCases';
import useDebounce from '../../../../hooks/useDebounce';
import { usePersistedRowsPerPage } from '../../../../hooks/usePersistedRowsPerPage';
import { useAppSelector } from '../../../../hooks/useReduxHook';
import useSubscribe, { Subscriber } from '../../../../hooks/useSubscribe';
import { CaseType, ICase } from '../../../../services/cases/types/ICase';
import { ClientTag, UserRoleName } from '../../../../services/identity/types/UserProfile';
import { formatDate } from '../../../../Utils/formatDate';
import { getStatusColorStyles } from '../../../../Utils/getColorForStatus';
import { getFriendlyName } from '../../../../Utils/getFriendlyName';
import { allCaseTypeOptions, caseStatusOptions, stateOptions } from '../../../../Utils/selectOptions';
import { getTextForStatus } from '../../../../Utils/getTextForStatus';
import CADataGrid, { ColumnDef, EllipsisCell, columnWidthPx, caGridStyles } from '../Components/CADataGrid';
import styles from './casesDataGrid.module.scss';
import { Status } from '@brands/services/cases/types/ICase';

type CasesView = 'allCases' | 'myCases';

const FILTER_MENU_PROPS = {
  PaperProps: {
    sx: {
      '& .MuiMenuItem-root': {
        fontFamily: 'Poppins, sans-serif',
        fontSize: '16px',
        fontWeight: 400,
        letterSpacing: '0.15px',
        lineHeight: '24px',
        color: '#172433',
      },
    },
  },
} as const;

// Derived from the same list that renders the Type dropdown, so the filter a
// care advisor sees and the case_type array actually sent can never drift.
const CASE_TYPE_OPTIONS = allCaseTypeOptions.map((option) => option.value as CaseType);

// Matches the status filter shown on the SuperAdmin cases list, so the same
// statuses are filterable regardless of role.
const DEFAULT_STATUS_PARAM = [
  Status.Upcoming,
  Status.AcceptedCareAdvisor,
  Status.AcceptedProvider,
  Status.InProgressProvider,
  Status.InProgressCareAdvisor,
  Status.CompletedByProvider,
];
const DEFAULT_TYPE_PARAM = CASE_TYPE_OPTIONS.filter((ct) => ct !== CaseType.smile_scan);
const selectFilterSx = {
  fontSize: 12,
  '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
};

const placeholderStyle: React.CSSProperties = { color: '#0d203599', fontStyle: 'normal', fontSize: 12 };
const filterLabelSx = { fontSize: 12, fontWeight: 400 };

const FIELD_TO_SORT_BY: Record<string, string> = {
  case_numeric_id: 'case_id',
  case_state: 'patient_current_state',
  patient: 'patient_full_name',
  provider: 'provider_full_name',
  care_advisor: 'care_advisor_full_name',
  collector: 'collector_full_name',
};

const SORTABLE_COLUMNS = [
  'created_at',
  'due_date',
  'type',
  'case_id',
  'patient_current_state',
  'patient_full_name',
  'client_tag',
  'provider_full_name',
  'care_advisor_full_name',
  'collector_full_name',
  'status',
];

const SESSION_STATE_KEY = 'dentistry-one:care-advisor:cases';
const MOBILE_GRID_COLUMN_WIDTHS: Record<string, number> = {
  created_at: 180,
  due_date: 180,
  type: 156,
  case_id: 132,
  patient_current_state: 160,
  patient_full_name: 180,
  client_tag: 180,
  client_division: 160,
  provider_full_name: 180,
  care_advisor_full_name: 180,
  collector_full_name: 180,
  status: 168,
};

const MOBILE_DAY_LABELS: Record<string, string> = {
  Tuesday: 'Tues',
  Thursday: 'Thurs',
};

function parseDateRangeValues(startValue: unknown, endValue: unknown): [Date, Date] | null {
  const start = typeof startValue === 'string' ? startValue : '';
  const end = typeof endValue === 'string' ? endValue : '';
  if (!start || !end) return null;

  const startDate = moment(start, 'YYYY-MM-DD', true).toDate();
  const endDate = moment(end, 'YYYY-MM-DD', true).toDate();
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) return null;

  return [startDate, endDate];
}

function getDueDateText(caseRow: ICase): string {
  const date = getDueDateMoment(caseRow);
  if (!date) return 'TBD';
  return date.format('MM/DD/YYYY hh:mm A');
}

function getDueDateMoment(caseRow: ICase): moment.Moment | null {
  const dateSource =
    caseRow.type === CaseType.oral_health_coaching ||
    caseRow.type === CaseType.phone_call_scheduled ||
    caseRow.type === CaseType.video_call_scheduled
      ? caseRow.video_conference_date
      : caseRow.started_at;
  if (!dateSource) return null;
  const date = moment.utc(dateSource).add(24, 'hours');
  if (!date.isValid()) return null;
  return date.tz(moment.tz.guess());
}

function getCreatedDateMoment(dateString: string): moment.Moment | null {
  const date = moment.utc(dateString);
  if (!date.isValid()) return null;
  return date.tz(moment.tz.guess());
}

function formatMobileGridDate(date: moment.Moment | null): JSX.Element {
  if (!date) return <span className={styles.mobileGridDate}>TBD</span>;
  const weekday = date.format('dddd');
  return (
    <span className={styles.mobileGridDate}>
      <span>{`${MOBILE_DAY_LABELS[weekday] ?? date.format('ddd')}, ${date.format('MMMM D')}`}</span>
      <span>{date.format('hh:mm A')}</span>
    </span>
  );
}

function buildColumnWidths(rows: ICase[]): Record<string, number> {
  const COLS = [
    { key: 'created_at', label: 'Requested date' },
    { key: 'due_date', label: 'Due date' },
    { key: 'type', label: 'Case type' },
    { key: 'case_id', label: 'Case ID' },
    { key: 'patient_current_state', label: 'Case state' },
    { key: 'patient_full_name', label: 'Patient' },
    { key: 'client_tag', label: 'Client tag(s)' },
    { key: 'client_division', label: 'Division' },
    { key: 'provider_full_name', label: 'Provider' },
    { key: 'care_advisor_full_name', label: 'Care Advisor' },
    { key: 'collector_full_name', label: 'Collector' },
    { key: 'status', label: 'Status' },
  ];
  const out: Record<string, number> = {};
  const stringsFor = (key: string): string[] =>
    rows.map((row) => {
      switch (key) {
        case 'created_at':
          return formatDate(row.created_at);
        case 'due_date':
          return getDueDateText(row);
        case 'type':
          return getFriendlyName(row.type);
        case 'case_id':
          return String(row.id);
        case 'patient_current_state':
          return row.patient_current_state || '';
        case 'patient_full_name':
          return row.patient?.full_name || '';
        case 'client_tag':
          return row.organization?.id === 'dentistry-one' ? row.client?.code || '' : '';
        case 'client_division':
          return row.client_division?.name || '';
        case 'provider_full_name':
          return row.provider?.full_name || '';
        case 'care_advisor_full_name':
          return row.care_advisor?.full_name || '';
        case 'collector_full_name':
          return (row as ICase & { collector?: { full_name?: string } | null }).collector?.full_name || '';
        case 'status':
          return getTextForStatus(UserRoleName.CareAdvisor, row.type, row.status);
        default:
          return '';
      }
    });
  COLS.forEach((c) => {
    const values = stringsFor(c.key);
    out[c.key] = columnWidthPx(c.label, values.length ? values : ['—']);
  });
  return out;
}

const CasesDataGrid: React.FC = () => {
  const navigate = useRoleNavigate();
  const location = useLocation();
  const { width } = useWindowSize();
  const isMobile = width < 768;
  const { userInfo } = useAppSelector(selectAuth);
  const savedState = useMemo(() => readSessionTableState(SESSION_STATE_KEY), []);

  // "View full history" on the consultation page deep-links here with a
  // ?patient_full_name= query param. Seed the patient filter from it and open
  // on the all-cases view so the patient's full history shows regardless of
  // which care advisor each case is assigned to.
  const [searchParams] = useSearchParams();
  const initialPatientFilter = searchParams.get('patient_full_name')?.trim() ?? '';
  const initialCasesView = initialPatientFilter
    ? 'allCases'
    : (getSessionString(savedState, 'cases_view', 'myCases') as CasesView);
  const initialSortBy = getSessionString(savedState, 'sort_by');

  const [casesView, setCasesView] = useState<CasesView>(
    initialCasesView === 'allCases' || initialCasesView === 'myCases' ? initialCasesView : 'myCases',
  );
  const [rowsPerPage, setRowsPerPage] = usePersistedRowsPerPage(TablePaginationKey.CaCases, isMobile ? 10 : 25);
  const [pageIndex, setPageIndex] = useState(() => Math.max(0, getSessionNumber(savedState, 'page', 1) - 1));
  const prevPageRef = useRef(pageIndex);
  const rowsPerPageHydratedRef = useRef(false);
  const incomingPatientPersistedRef = useRef(false);
  const [sortBy, setSortBy] = useState<string | null>(SORTABLE_COLUMNS.includes(initialSortBy) ? initialSortBy : null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | null>(
    getSessionString(savedState, 'sort_direction') === 'asc'
      ? 'asc'
      : getSessionString(savedState, 'sort_direction') === 'desc'
      ? 'desc'
      : null,
  );
  const [filterCaseId, setFilterCaseId] = useState(() => getSessionString(savedState, 'case_id'));
  const [filterCaseState, setFilterCaseState] = useState<string[]>(() => getSessionArray(savedState, 'case_state'));
  const [filterPatient, setFilterPatient] = useState(
    () => initialPatientFilter || getSessionString(savedState, 'patient_full_name'),
  );
  const [filterProvider, setFilterProvider] = useState(() => getSessionString(savedState, 'provider_full_name'));
  const [filterCareAdvisor, setFilterCareAdvisor] = useState(() =>
    getSessionString(savedState, 'care_advisor_full_name'),
  );
  const [filterType, setFilterType] = useState<string[]>(() => getSessionArray(savedState, 'case_type'));
  const [filterClientTag, setFilterClientTag] = useState<string[]>(() => getSessionArray(savedState, 'client_tag'));
  const [filterClientDivisionIds, setFilterClientDivisionIds] = useState<string[]>(() =>
    getSessionArray(savedState, 'client_division_ids'),
  );
  const [filterCollector, setFilterCollector] = useState(() => getSessionString(savedState, 'collector_full_name'));
  const [allClientTags, setAllClientTags] = useState<ClientTag[]>([]);

  useEffect(() => {
    listAllClientTags(UserRoleName.CareAdvisor)
      .then(({ nodes }) => setAllClientTags(nodes.filter((tag) => Boolean(tag.code))))
      .catch(() => setAllClientTags([]));
  }, []);

  const clientTagOptions = useMemo(
    () => allClientTags.map((tag) => ({ value: tag.code as string, label: tag.name })),
    [allClientTags],
  );

  const selectedClientTagIdsForDivision = useMemo(
    () => allClientTags.filter((tag) => filterClientTag.includes(tag.code || '')).map((tag) => tag.id),
    [allClientTags, filterClientTag],
  );

  const divisionFilterOptions = useMemo(
    () => getClientDivisionOptions(allClientTags, selectedClientTagIdsForDivision),
    [allClientTags, selectedClientTagIdsForDivision],
  );
  const [filterStatus, setFilterStatus] = useState<string[]>(() => {
    const statuses = getSessionArray(savedState, 'status');
    return statuses.length ? statuses : [];
  });
  const [filterScheduledDateRange, setFilterScheduledDateRange] = useState<[Date, Date] | null>(() =>
    parseDateRangeValues(savedState.scheduled_date_start, savedState.scheduled_date_end),
  );
  const [filterDueDateRange, setFilterDueDateRange] = useState<[Date, Date] | null>(() =>
    parseDateRangeValues(savedState.due_date_start, savedState.due_date_end),
  );

  const FILTER_DEBOUNCE_MS = 1000;
  const debouncedFilterCaseId = useDebounce(filterCaseId, FILTER_DEBOUNCE_MS);
  const debouncedFilterCaseState = useDebounce(filterCaseState, FILTER_DEBOUNCE_MS);
  const debouncedFilterPatient = useDebounce(filterPatient, FILTER_DEBOUNCE_MS);
  const debouncedFilterProvider = useDebounce(filterProvider, FILTER_DEBOUNCE_MS);
  const debouncedFilterCareAdvisor = useDebounce(filterCareAdvisor, FILTER_DEBOUNCE_MS);
  const debouncedFilterType = useDebounce(filterType, FILTER_DEBOUNCE_MS);
  const debouncedFilterClientTag = useDebounce(filterClientTag, FILTER_DEBOUNCE_MS);
  const debouncedFilterClientDivisionIds = useDebounce(filterClientDivisionIds, FILTER_DEBOUNCE_MS);
  const debouncedFilterCollector = useDebounce(filterCollector, FILTER_DEBOUNCE_MS);
  const debouncedFilterStatus = useDebounce(filterStatus, FILTER_DEBOUNCE_MS);
  const debouncedFilterScheduledDateRange = useDebounce(filterScheduledDateRange, FILTER_DEBOUNCE_MS);
  const debouncedFilterDueDateRange = useDebounce(filterDueDateRange, FILTER_DEBOUNCE_MS);

  const updateCaseState = useCallback(
    (
      overrides: Partial<{
        casesView: CasesView;
        page: number;
        rowsPerPage: number;
        sortBy: string | null;
        sortDirection: 'asc' | 'desc' | null;
        caseId: string;
        caseState: string[];
        patient: string;
        provider: string;
        careAdvisor: string;
        type: string[];
        clientTag: string[];
        clientDivisionIds: string[];
        collector: string;
        status: string[];
        scheduledDateRange: [Date, Date] | null;
        dueDateRange: [Date, Date] | null;
      }> = {},
    ) => {
      const nextScheduledDateRange = Object.prototype.hasOwnProperty.call(overrides, 'scheduledDateRange')
        ? overrides.scheduledDateRange
        : filterScheduledDateRange;
      const nextDueDateRange = Object.prototype.hasOwnProperty.call(overrides, 'dueDateRange')
        ? overrides.dueDateRange
        : filterDueDateRange;
      const nextSortBy = Object.prototype.hasOwnProperty.call(overrides, 'sortBy') ? overrides.sortBy : sortBy;
      const nextSortDirection = Object.prototype.hasOwnProperty.call(overrides, 'sortDirection')
        ? overrides.sortDirection
        : sortDirection;
      writeSessionTableState(SESSION_STATE_KEY, {
        cases_view: overrides.casesView ?? casesView,
        page: (overrides.page ?? pageIndex) + 1,
        rowsPerPage: overrides.rowsPerPage ?? rowsPerPage,
        sort_by: nextSortBy ?? undefined,
        sort_direction: nextSortDirection ?? undefined,
        case_id: overrides.caseId ?? filterCaseId,
        case_state: overrides.caseState ?? filterCaseState,
        patient_full_name: overrides.patient ?? filterPatient,
        provider_full_name: overrides.provider ?? filterProvider,
        care_advisor_full_name: overrides.careAdvisor ?? filterCareAdvisor,
        case_type: overrides.type ?? filterType,
        client_tag: overrides.clientTag ?? filterClientTag,
        client_division_ids: overrides.clientDivisionIds ?? filterClientDivisionIds,
        collector_full_name: overrides.collector ?? filterCollector,
        status: overrides.status ?? filterStatus,
        scheduled_date_start: nextScheduledDateRange?.[0]?.toISOString().split('T')[0],
        scheduled_date_end: nextScheduledDateRange?.[1]?.toISOString().split('T')[0],
        due_date_start: nextDueDateRange?.[0]?.toISOString().split('T')[0],
        due_date_end: nextDueDateRange?.[1]?.toISOString().split('T')[0],
      });
    },
    [
      casesView,
      filterCareAdvisor,
      filterCaseId,
      filterCaseState,
      filterClientTag,
      filterClientDivisionIds,
      filterCollector,
      filterDueDateRange,
      filterPatient,
      filterProvider,
      filterScheduledDateRange,
      filterStatus,
      filterType,
      pageIndex,
      rowsPerPage,
      sortBy,
      sortDirection,
    ],
  );

  useEffect(() => {
    if (rowsPerPageHydratedRef.current) return;
    rowsPerPageHydratedRef.current = true;
    const rowsPerPageFromSession = getSessionNumber(savedState, 'rowsPerPage', rowsPerPage);
    if (rowsPerPageFromSession > 0 && rowsPerPageFromSession !== rowsPerPage) {
      setRowsPerPage(rowsPerPageFromSession);
    }
  }, [rowsPerPage, savedState, setRowsPerPage]);

  useEffect(() => {
    if (!initialPatientFilter || incomingPatientPersistedRef.current) return;
    incomingPatientPersistedRef.current = true;
    updateCaseState({
      casesView: 'allCases',
      patient: initialPatientFilter,
      page: 0,
      rowsPerPage: getSessionNumber(savedState, 'rowsPerPage', rowsPerPage),
    });
  }, [initialPatientFilter, rowsPerPage, savedState, updateCaseState]);

  const resetPage = useCallback(() => {
    setPageIndex(0);
    prevPageRef.current = 0;
  }, []);

  const params = useMemo<IListCasesParams>(() => {
    const trimmedCaseId = debouncedFilterCaseId.trim();
    const caseIdNum = Number(trimmedCaseId);
    const caseIdFilter = trimmedCaseId && Number.isInteger(caseIdNum) && caseIdNum > 0 ? caseIdNum : null;
    const base: IListCasesParams = {
      sort_by: sortBy ? FIELD_TO_SORT_BY[sortBy] ?? sortBy : 'created_at',
      sort_direction: sortDirection ?? 'desc',
      limit: rowsPerPage,
      // Looking a case up by its id has to find it whatever its type and
      // status, so the two invisible defaults apply only when not searching
      // by id — the type list covers only the types offered today, and the
      // status list drops new, completed and canceled. An explicit filter
      // below still wins either way.
      ...(caseIdFilter ? { case_id: caseIdFilter } : { status: DEFAULT_STATUS_PARAM, case_type: DEFAULT_TYPE_PARAM }),
    };
    if (casesView === 'myCases') base.care_advisor_id = Number(userInfo.id);
    if (debouncedFilterCaseState.length > 0) base.case_state = debouncedFilterCaseState;
    if (debouncedFilterPatient) base.patient_full_name = debouncedFilterPatient;
    if (debouncedFilterProvider) base.provider_full_name = debouncedFilterProvider;
    if (debouncedFilterCareAdvisor) base.care_advisor_full_name_filter = debouncedFilterCareAdvisor;
    if (debouncedFilterType.length > 0) base.case_type = debouncedFilterType as CaseType[];
    if (debouncedFilterClientTag.length > 0) base.client_tag = debouncedFilterClientTag;
    if (debouncedFilterClientDivisionIds.length > 0) {
      base.client_division_ids = debouncedFilterClientDivisionIds.map(Number);
    }
    if (debouncedFilterStatus.length > 0) {
      base.status = debouncedFilterStatus;
    }
    if (debouncedFilterDueDateRange) {
      base.consultation_date_start = debouncedFilterDueDateRange[0].toISOString().split('T')[0];
      base.consultation_date_end = debouncedFilterDueDateRange[1].toISOString().split('T')[0];
    }
    if (debouncedFilterCollector) base.collector_full_name = debouncedFilterCollector;
    if (debouncedFilterScheduledDateRange) {
      base.created_at_start = debouncedFilterScheduledDateRange[0].toISOString().split('T')[0];
      base.created_at_end = debouncedFilterScheduledDateRange[1].toISOString().split('T')[0];
    }

    return base;
  }, [
    sortBy,
    sortDirection,
    rowsPerPage,
    casesView,
    userInfo.id,
    debouncedFilterCaseId,
    debouncedFilterCaseState,
    debouncedFilterPatient,
    debouncedFilterProvider,
    debouncedFilterCareAdvisor,
    debouncedFilterType,
    debouncedFilterClientTag,
    debouncedFilterClientDivisionIds,
    debouncedFilterStatus,
    debouncedFilterDueDateRange,
    debouncedFilterCollector,
    debouncedFilterScheduledDateRange,
  ]);

  const { cases, totalNewCases, reloadAll, loadNextPage, loadPreviousPage, dataLoading } = useCareAdvisorCases(
    params,
    true,
  );

  const columnWidths = useMemo(() => buildColumnWidths(cases), [cases]);

  const subscriber: Subscriber = useMemo(
    () => ({
      onMessage: (message: Record<string, any>) => {
        if (['case_updated', 'case_cancelled', 'case_new_message_created', 'case_created'].includes(message.command)) {
          reloadAll();
        }
      },
      commands: ['case_created', 'case_updated', 'case_cancelled', 'case_new_message_created'],
    }),
    [reloadAll],
  );

  const { subscribe, unsubscribe } = useSubscribe();
  useEffect(() => {
    const subscriptionId = subscribe(subscriber);
    return () => unsubscribe(subscriptionId);
  }, [subscribe, unsubscribe, subscriber]);

  const handleSort = useCallback(
    (key: string) => {
      resetPage();
      if (sortBy !== key) {
        setSortBy(key);
        setSortDirection('asc');
        updateCaseState({ sortBy: key, sortDirection: 'asc', page: 0 });
        return;
      }
      if (sortDirection === 'asc') {
        setSortDirection('desc');
        updateCaseState({ sortBy: key, sortDirection: 'desc', page: 0 });
        return;
      }
      setSortBy(null);
      setSortDirection(null);
      updateCaseState({ sortBy: null, sortDirection: null, page: 0 });
    },
    [sortBy, sortDirection, resetPage, updateCaseState],
  );

  const handlePageChange = useCallback(
    (nextPage: number) => {
      if (nextPage > prevPageRef.current) loadNextPage();
      else if (nextPage < prevPageRef.current) loadPreviousPage();
      prevPageRef.current = nextPage;
      setPageIndex(nextPage);
      updateCaseState({ page: nextPage });
    },
    [loadNextPage, loadPreviousPage, updateCaseState],
  );

  const handleDateRangeFilterChange = useCallback(
    (key: string, value: [Date, Date] | null) => {
      resetPage();
      if (key === 'scheduledDate') setFilterScheduledDateRange(value);
      else if (key === 'dueDate') setFilterDueDateRange(value);
      updateCaseState({
        page: 0,
        ...(key === 'scheduledDate' ? { scheduledDateRange: value } : {}),
        ...(key === 'dueDate' ? { dueDateRange: value } : {}),
      });
    },
    [resetPage, updateCaseState],
  );

  const handleRowClick = useCallback(
    (row: ICase) => {
      sessionStorage.setItem('lastNonConsultationPagePath', `${location.pathname}${location.hash}`);
      if (row.type === CaseType.smile_scan && row.status === Status.Completed) {
        navigate(`/smile-scan-report/${row.id}`);
      } else if (row.type === CaseType.messaging) {
        // A case-id lookup can surface a retired type, and the consultation
        // page has no branch for messaging — it renders blank. Route it the
        // way the My Cases grid does.
        navigate(`/case-chat/${row.id}`);
      } else {
        navigate(`/consultation-page/${row.id}`);
      }
    },
    [location.hash, location.pathname, navigate],
  );

  const columns: ColumnDef<ICase>[] = useMemo(
    () => [
      {
        key: 'created_at',
        label: 'Requested date',
        widthPx: columnWidths.created_at,
        renderCell: (row) =>
          isMobile ? (
            formatMobileGridDate(getCreatedDateMoment(row.created_at))
          ) : (
            <EllipsisCell text={formatDate(row.created_at)} />
          ),
        filterContent: (
          <MinimalDateRangePicker
            filterKey="scheduledDate"
            handleDateRangeFilterChange={handleDateRangeFilterChange}
            selectedRange={filterScheduledDateRange}
            width="100%"
            placeholder="Select date range"
          />
        ),
      },
      {
        key: 'due_date',
        label: 'Due date',
        widthPx: columnWidths.due_date,
        renderCell: (row) =>
          isMobile ? formatMobileGridDate(getDueDateMoment(row)) : <EllipsisCell text={getDueDateText(row)} />,
        filterContent: (
          <MinimalDateRangePicker
            filterKey="dueDate"
            handleDateRangeFilterChange={handleDateRangeFilterChange}
            selectedRange={filterDueDateRange}
            width="100%"
            placeholder="Select date range"
          />
        ),
      },
      {
        key: 'type',
        label: 'Case type',
        widthPx: columnWidths.type,
        renderCell: (row) => <EllipsisCell text={getFriendlyName(row.type)} />,
        filterContent: (
          <Select
            size="small"
            multiple
            value={filterType}
            onChange={(e: SelectChangeEvent<typeof filterType>) => {
              const v = e.target.value;
              const nextValue = typeof v === 'string' ? v.split(',') : v;
              setFilterType(nextValue);
              resetPage();
              updateCaseState({ type: nextValue, page: 0 });
            }}
            displayEmpty
            fullWidth
            MenuProps={FILTER_MENU_PROPS}
            sx={selectFilterSx}
            renderValue={(selected) =>
              selected.length === 0 ? (
                <em style={placeholderStyle}>Type</em>
              ) : (
                selected.map((v) => allCaseTypeOptions.find((o) => o.value === v)?.label ?? v).join(', ')
              )
            }
          >
            {allCaseTypeOptions.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                <Checkbox checked={filterType.includes(opt.value)} size="small" />
                <ListItemText primary={opt.label} />
              </MenuItem>
            ))}
          </Select>
        ),
      },
      {
        key: 'case_id',
        label: 'Case ID',
        widthPx: columnWidths.case_id,
        renderCell: (row) => <EllipsisCell text={String(row.id)} />,
        filterContent: (
          <TextField
            variant="standard"
            fullWidth
            label="Case ID"
            value={filterCaseId}
            onChange={(e) => {
              setFilterCaseId(e.target.value);
              resetPage();
              updateCaseState({ caseId: e.target.value, page: 0 });
            }}
            InputProps={{ disableUnderline: true, sx: { fontSize: 12, px: 0.5 } }}
            InputLabelProps={{ shrink: Boolean(filterCaseId), sx: filterLabelSx }}
            inputProps={{ inputMode: 'numeric' }}
          />
        ),
      },
      {
        key: 'patient_current_state',
        label: 'Case state',
        widthPx: columnWidths.patient_current_state,
        renderCell: (row) => <EllipsisCell text={row.patient_current_state || ''} />,
        filterContent: (
          <Select
            size="small"
            multiple
            value={filterCaseState}
            onChange={(e: SelectChangeEvent<typeof filterCaseState>) => {
              const v = e.target.value;
              const nextValue = typeof v === 'string' ? v.split(',') : v;
              setFilterCaseState(nextValue);
              resetPage();
              updateCaseState({ caseState: nextValue, page: 0 });
            }}
            displayEmpty
            fullWidth
            MenuProps={FILTER_MENU_PROPS}
            sx={selectFilterSx}
            renderValue={(selected) =>
              selected.length === 0 ? (
                <em style={placeholderStyle}>Case state</em>
              ) : (
                selected.map((v) => stateOptions.find((o) => o.value === v)?.label ?? v).join(', ')
              )
            }
          >
            {stateOptions.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                <Checkbox checked={filterCaseState.includes(opt.value)} size="small" />
                <ListItemText primary={opt.label} />
              </MenuItem>
            ))}
          </Select>
        ),
      },
      {
        key: 'patient_full_name',
        label: 'Patient',
        widthPx: columnWidths.patient_full_name,
        renderCell: (row) => <EllipsisCell text={row.patient?.full_name || ''} />,
        filterContent: (
          <TextField
            variant="standard"
            fullWidth
            label="Patient name"
            value={filterPatient}
            onChange={(e) => {
              setFilterPatient(e.target.value);
              resetPage();
              updateCaseState({ patient: e.target.value, page: 0 });
            }}
            InputProps={{ disableUnderline: true, sx: { fontSize: 12, px: 0.5 } }}
            InputLabelProps={{ shrink: Boolean(filterPatient), sx: filterLabelSx }}
          />
        ),
      },
      {
        key: 'client_tag',
        label: 'Client tag(s)',
        widthPx: columnWidths.client_tag,
        renderCell: (row) => (
          // The case's own client, stamped at creation -- not the patient's
          // current markers, which can drift to a different client after
          // the case was created and would then mislabel this case.
          <EllipsisCell text={row.organization?.id === 'dentistry-one' ? row.client?.code || '' : ''} />
        ),
        filterContent: (
          <Select
            size="small"
            multiple
            displayEmpty
            fullWidth
            value={filterClientTag}
            onChange={(e: SelectChangeEvent<typeof filterClientTag>) => {
              const v = e.target.value;
              const nextValue = typeof v === 'string' ? v.split(',') : v;
              setFilterClientTag(nextValue);
              setFilterClientDivisionIds([]);
              resetPage();
              updateCaseState({ clientTag: nextValue, clientDivisionIds: [], page: 0 });
            }}
            MenuProps={FILTER_MENU_PROPS}
            sx={selectFilterSx}
            renderValue={(selected) =>
              selected.length === 0 ? (
                <em style={placeholderStyle}>Client tag(s)</em>
              ) : (
                selected.map((v) => clientTagOptions.find((o) => o.value === v)?.label ?? v).join(', ')
              )
            }
          >
            {clientTagOptions.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                <Checkbox checked={filterClientTag.includes(opt.value)} size="small" />
                <ListItemText primary={opt.label} />
              </MenuItem>
            ))}
          </Select>
        ),
      },
      {
        key: 'client_division',
        label: 'Division',
        widthPx: columnWidths.client_division,
        sortable: false,
        renderCell: (row) => <EllipsisCell text={row.client_division?.name || ''} />,
        filterContent: (
          <Select
            size="small"
            multiple
            displayEmpty
            fullWidth
            disabled={filterClientTag.length === 0}
            value={filterClientDivisionIds}
            onChange={(e: SelectChangeEvent<typeof filterClientDivisionIds>) => {
              const v = e.target.value;
              const nextValue = typeof v === 'string' ? v.split(',') : v;
              setFilterClientDivisionIds(nextValue);
              resetPage();
              updateCaseState({ clientDivisionIds: nextValue, page: 0 });
            }}
            MenuProps={FILTER_MENU_PROPS}
            sx={selectFilterSx}
            renderValue={(selected) =>
              selected.length === 0 ? (
                <em style={placeholderStyle}>
                  {filterClientTag.length ? 'Division' : 'Select a client tag first'}
                </em>
              ) : (
                selected.map((v) => divisionFilterOptions.find((o) => o.id === v)?.label ?? v).join(', ')
              )
            }
          >
            {divisionFilterOptions.map((opt) => (
              <MenuItem key={opt.id} value={opt.id}>
                <Checkbox checked={filterClientDivisionIds.includes(opt.id)} size="small" />
                <ListItemText primary={opt.label} />
              </MenuItem>
            ))}
          </Select>
        ),
      },
      {
        key: 'provider_full_name',
        label: 'Provider',
        widthPx: columnWidths.provider_full_name,
        renderCell: (row) => <EllipsisCell text={row.provider?.full_name || ''} />,
        filterContent: (
          <TextField
            variant="standard"
            fullWidth
            label="Provider name"
            value={filterProvider}
            onChange={(e) => {
              setFilterProvider(e.target.value);
              resetPage();
              updateCaseState({ provider: e.target.value, page: 0 });
            }}
            InputProps={{ disableUnderline: true, sx: { fontSize: 12, px: 0.5 } }}
            InputLabelProps={{ shrink: Boolean(filterProvider), sx: filterLabelSx }}
          />
        ),
      },
      {
        key: 'care_advisor_full_name',
        label: 'Care Advisor',
        widthPx: columnWidths.care_advisor_full_name,
        renderCell: (row) => <EllipsisCell text={row.care_advisor?.full_name || ''} />,
        filterContent: (
          <TextField
            variant="standard"
            fullWidth
            label="Care Advisor name"
            value={filterCareAdvisor}
            onChange={(e) => {
              setFilterCareAdvisor(e.target.value);
              resetPage();
              updateCaseState({ careAdvisor: e.target.value, page: 0 });
            }}
            InputProps={{ disableUnderline: true, sx: { fontSize: 12, px: 0.5 } }}
            InputLabelProps={{ shrink: Boolean(filterCareAdvisor), sx: filterLabelSx }}
          />
        ),
      },
      {
        key: 'collector_full_name',
        label: 'Collector',
        widthPx: columnWidths.collector_full_name,
        renderCell: (row) => (
          <EllipsisCell
            text={(row as ICase & { collector?: { full_name?: string } | null }).collector?.full_name || ''}
          />
        ),
        filterContent: (
          <TextField
            variant="standard"
            fullWidth
            label="Collector name"
            value={filterCollector}
            onChange={(e) => {
              setFilterCollector(e.target.value);
              resetPage();
              updateCaseState({ collector: e.target.value, page: 0 });
            }}
            InputProps={{ disableUnderline: true, sx: { fontSize: 12, px: 0.5 } }}
            InputLabelProps={{ shrink: Boolean(filterCollector), sx: filterLabelSx }}
          />
        ),
      },
      {
        key: 'status',
        label: 'Status',
        widthPx: columnWidths.status,
        renderCell: (row) => (
          <span className={caGridStyles.statusChip} style={getStatusColorStyles(row.status)}>
            {getTextForStatus(UserRoleName.CareAdvisor, row.type, row.status)}
          </span>
        ),
        filterContent: (
          <Select
            size="small"
            multiple
            value={filterStatus}
            onChange={(e: SelectChangeEvent<typeof filterStatus>) => {
              const v = e.target.value;
              const nextValue = typeof v === 'string' ? v.split(',') : v;
              setFilterStatus(nextValue);
              resetPage();
              updateCaseState({ status: nextValue, page: 0 });
            }}
            displayEmpty
            fullWidth
            MenuProps={FILTER_MENU_PROPS}
            sx={selectFilterSx}
            renderValue={(selected) =>
              selected.length === 0 ? (
                <em style={placeholderStyle}>Status</em>
              ) : (
                selected.map((v) => caseStatusOptions.find((o) => o.value === v)?.label ?? v).join(', ')
              )
            }
          >
            {caseStatusOptions.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                <Checkbox checked={filterStatus.includes(opt.value)} size="small" />
                <ListItemText primary={opt.label} />
              </MenuItem>
            ))}
          </Select>
        ),
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
    ],
    [
      columnWidths,
      filterScheduledDateRange,
      filterDueDateRange,
      filterType,
      filterCaseId,
      filterCaseState,
      filterPatient,
      filterClientTag,
      filterClientDivisionIds,
      clientTagOptions,
      divisionFilterOptions,
      filterProvider,
      filterCareAdvisor,
      filterCollector,
      filterStatus,
      isMobile,
      resetPage,
      updateCaseState,
    ],
  );

  const gridColumns = useMemo(
    () =>
      isMobile
        ? columns.map((column) => ({
            ...column,
            widthPx: MOBILE_GRID_COLUMN_WIDTHS[column.key] ?? Math.max(140, Math.min(column.widthPx, 180)),
          }))
        : columns,
    [columns, isMobile],
  );

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>Cases</h1>
        <div className={styles.headerActions}>
          <Select
            size="small"
            value={casesView}
            onChange={(e: SelectChangeEvent) => {
              const nextCasesView = e.target.value as CasesView;
              setCasesView(nextCasesView);
              resetPage();
              updateCaseState({ casesView: nextCasesView, page: 0 });
            }}
            className={styles.viewSelect}
            IconComponent={HiChevronDown}
            sx={
              isMobile
                ? {
                    height: 37,
                    borderRadius: '7px',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#172433' },
                    '& .MuiSelect-select': {
                      py: '7px',
                      pl: '14px',
                      pr: '34px',
                      fontSize: 15,
                      fontWeight: 400,
                      lineHeight: '22px',
                    },
                    '& .MuiSelect-icon': { color: '#172433', right: 8 },
                  }
                : undefined
            }
          >
            <MenuItem value="allCases">All cases</MenuItem>
            <MenuItem value="myCases">My cases</MenuItem>
          </Select>
          <button type="button" className={styles.createBtn} onClick={() => navigate('/patient-select')}>
            <FiPlus />
            <span>{isMobile ? 'New case' : 'Create new case'}</span>
          </button>
        </div>
      </div>

      <CADataGrid
        columns={gridColumns}
        rows={cases}
        onRowClick={handleRowClick}
        sortBy={sortBy}
        sortDirection={sortDirection}
        onSort={handleSort}
        count={totalNewCases}
        page={pageIndex}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[10, 25, 50, 100]}
        onPageChange={handlePageChange}
        onRowsPerPageChange={(rpp) => {
          setRowsPerPage(rpp);
          resetPage();
          updateCaseState({ rowsPerPage: rpp, page: 0 });
        }}
        loading={dataLoading}
        isMobile={isMobile}
        showManageColumns
        columnVisibilityKey={ColumnVisibilityKey.CaCases}
      />
    </div>
  );
};

export default CasesDataGrid;
